import { Component } from '@theme/component';

/**
 * Generic Bundle Contents — read-only list of a product's `custom.bundle_generic`
 * products, rendered with the product buy flow's tile + "Learn more" modal
 * markup (`.cb__*`). Only the modal behaviour lives here: open/close with the
 * animated exit, prev/next between products and the per-product gallery dots.
 * Adding to cart is handled by the product form (see product-form.js).
 *
 * @extends {Component}
 */
class GenericBundleContents extends Component {
  /** @type {HTMLDialogElement|null} */ #modal = null;
  /** @type {HTMLElement[]} */ #modalPanels = [];
  /** @type {string[]} */ #modalIds = [];
  #activeId = '';

  connectedCallback() {
    super.connectedCallback();

    this.#modal = /** @type {HTMLDialogElement|null} */ (this.querySelector('[data-cb-modal]'));
    this.#modalPanels = Array.from(this.querySelectorAll('.cb__modal-panel'));
    this.#modalIds = this.#modalPanels.map((panel) => panel.dataset.wineId || '');

    if (this.#modal) {
      this.#modal.addEventListener('click', (event) => {
        if (event.target === this.#modal) this.closeModal();
      });
      // ESC: animate the close instead of the native instant dismiss.
      this.#modal.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeModal();
      });
      // Release the scroll lock however the dialog closes (button, backdrop, ESC).
      this.#modal.addEventListener('close', () => {
        document.documentElement.removeAttribute('scroll-lock');
      });
    }

    for (const gallery of this.querySelectorAll('.cb__modal-gallery')) {
      gallery.addEventListener('scroll', () => this.#syncDots(gallery), { passive: true });
    }
  }

  /** @param {Event} event */
  openModal(event) {
    // The whole tile is the trigger, but its other controls (case-builder
    // steppers) keep their own behaviour. Learn more's click bubbles here.
    const target = /** @type {Element} */ (event.target);
    const control = target?.closest('button, a, input, select');
    if (control && !control.classList.contains('cb__learn-more')) return;
    const host = target?.closest('[data-wine-id]');
    if (!host) return;
    this.#show(/** @type {HTMLElement} */ (host).dataset.wineId || '');
    this.#modal?.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
  }

  closeModal() {
    const dialog = this.#modal;
    if (!dialog || !dialog.open || dialog.classList.contains('cb--closing')) return;
    // Play the reverse (exit) animation, then actually close the dialog.
    dialog.classList.add('cb--closing');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      dialog.removeEventListener('animationend', onEnd);
      dialog.classList.remove('cb--closing');
      if (dialog.open) dialog.close();
    };
    const onEnd = (/** @type {AnimationEvent} */ event) => {
      if (event.animationName === 'cbModalBackdropOut') finish();
    };
    dialog.addEventListener('animationend', onEnd);
    setTimeout(finish, 400); // fallback if animationend doesn't fire (e.g. reduced motion)
  }

  /** @param {Event} event */
  galleryDot(event) {
    const button = /** @type {HTMLElement|null} */ (/** @type {Element} */ (event.target)?.closest('[data-index]'));
    if (!button) return;
    const gallery = button.closest('.cb__modal-media')?.querySelector('.cb__modal-gallery');
    const image = /** @type {HTMLElement|undefined} */ (gallery?.children[parseInt(button.dataset.index || '0', 10)]);
    if (gallery && image) gallery.scrollTo({ left: image.offsetLeft, behavior: 'smooth' });
  }

  prevWine() {
    this.#step(-1);
  }

  nextWine() {
    this.#step(1);
  }

  /** @param {Element} gallery */
  #syncDots(gallery) {
    const dots = gallery.closest('.cb__modal-media')?.querySelectorAll('.cb__modal-dot');
    if (!dots || !dots.length) return;
    let active = 0;
    let best = Infinity;
    Array.from(gallery.children).forEach((child, index) => {
      const distance = Math.abs(/** @type {HTMLElement} */ (child).offsetLeft - gallery.scrollLeft);
      if (distance < best) {
        best = distance;
        active = index;
      }
    });
    dots.forEach((dot, index) => dot.setAttribute('aria-selected', String(index === active)));
  }

  /** @param {number} delta */
  #step(delta) {
    const index = this.#modalIds.indexOf(this.#activeId);
    if (index === -1) return;
    const length = this.#modalIds.length;
    this.#show(this.#modalIds[(index + delta + length) % length], true);
  }

  /**
   * @param {string} id
   * @param {boolean} [animate] cross-fade the swap (nav between products) vs instant (initial open)
   */
  #show(id, animate = false) {
    const panels = /** @type {HTMLElement|null} */ (this.querySelector('.cb__modal-panels'));
    const apply = () => {
      this.#activeId = id;
      for (const panel of this.#modalPanels) panel.hidden = panel.dataset.wineId !== id;
    };
    if (!animate || !panels) {
      apply();
      return;
    }
    panels.classList.add('cb--fading');
    setTimeout(() => {
      apply();
      requestAnimationFrame(() => panels.classList.remove('cb--fading'));
    }, 180);
  }
}

if (!customElements.get('generic-bundle-contents')) {
  customElements.define('generic-bundle-contents', GenericBundleContents);
}
